/**
 * Staged (not yet published) product suggestions, per booking.
 *
 * Suggestions ARE rows in the customer's cart, so they must not go upstream
 * while the consultation is running — the astrologer's picks are published in
 * one batch when they finalize the note on the appointment page. That means a
 * pick made inside the video room has to outlive the room itself: the console
 * unmounts on "End Consultation" and the astrologer finalizes from a different
 * route (often after a reload, sometimes hours later). React state can't carry
 * that, so the staging area lives in localStorage.
 *
 * sessionStorage would die with the tab and lose picks the astrologer already
 * made; the entries here are cleared as soon as commit() publishes them, and
 * anything left behind for over a month is dropped on read.
 */

const KEY_PREFIX = 'astro:staged-suggestions:v1:'
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

const storageKey = (bookingId) => `${KEY_PREFIX}${bookingId}`
const empty = () => ({ staged: [], removals: [] })

/** @returns {{staged: object[], removals: (number|string)[]}} */
export function readStagedSuggestions(bookingId) {
  if (!bookingId || typeof window === 'undefined') return empty()
  try {
    const raw = window.localStorage.getItem(storageKey(bookingId))
    if (!raw) return empty()
    const parsed = JSON.parse(raw)
    if (!parsed || Date.now() - Number(parsed.updatedAt || 0) > MAX_AGE_MS) {
      window.localStorage.removeItem(storageKey(bookingId))
      return empty()
    }
    return {
      staged: Array.isArray(parsed.staged) ? parsed.staged : [],
      removals: Array.isArray(parsed.removals) ? parsed.removals : [],
    }
  } catch {
    // Corrupt entry / storage blocked — start clean rather than break the panel.
    return empty()
  }
}

export function writeStagedSuggestions(bookingId, { staged = [], removals = [] } = {}) {
  if (!bookingId || typeof window === 'undefined') return
  try {
    if (!staged.length && !removals.length) {
      window.localStorage.removeItem(storageKey(bookingId))
      return
    }
    window.localStorage.setItem(
      storageKey(bookingId),
      JSON.stringify({ updatedAt: Date.now(), staged, removals }),
    )
  } catch {
    // Private mode / quota: staging still works for the current page, it just
    // won't survive the trip back from the video room.
  }
}
